import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2'
import { catalogApi, type CategoryTreeNode } from '@/api/endpoints'
import { useWarehouse } from '@/context/WarehouseContext'
import { useWarehouseScopedUrl } from '@/features/warehouse/useWarehouseScopedUrl'

interface CatalogDropdownProps {
  onClose: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

const CatalogDropdown = ({ onClose, onMouseEnter, onMouseLeave }: CatalogDropdownProps) => {
  const { selectedWarehouseId } = useWarehouse()
  const withWarehouse = useWarehouseScopedUrl()
  const [categories, setCategories] = useState<CategoryTreeNode[]>([])
  const [activeCategory, setActiveCategory] = useState<CategoryTreeNode | null>(null)
  /** Стек «родителей»: каждый элемент — категория, в детей которой мы зашли. Назад = снять один уровень. */
  const [drillStack, setDrillStack] = useState<CategoryTreeNode[]>([])
  const [loading, setLoading] = useState(true)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setLoading(true)
    setActiveCategory(null)
    setDrillStack([])
    catalogApi
      .getCategoriesTree()
      .then((data) => {
        setCategories(data)
        if (data.length > 0) setActiveCategory(data[0])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [selectedWarehouseId])

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => setIsVisible(true))
    return () => window.cancelAnimationFrame(frameId)
  }, [])

  const currentParent =
    drillStack.length > 0 ? drillStack[drillStack.length - 1] : activeCategory
  const shownChildren: CategoryTreeNode[] = currentParent?.children ?? []

  const goBack = () => setDrillStack((prev) => prev.slice(0, -1))

  return (
    <div
      className={`hidden lg:absolute top-full left-0 mt-1 z-50 lg:flex sm:h-96 lg:h-175 bg-white rounded-xl shadow-2xl border border-gray-200 w-full transition-all duration-200 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="w-60 border-r overflow-y-auto border-[#A6A6A650] p-5 rounded-l-xl">
        <div className="flex flex-col gap-y-2">
          {loading ? (
            Array.from({ length: 7 }).map((_, index) => (
              <div
                key={`catalog-dropdown-left-skeleton-${index}`}
                className={`flex items-center justify-between rounded-[10px] px-3.75 h-10.75 ${
                  index === 0 ? 'bg-under-header-color' : ''
                }`}
              >
                <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-4 animate-pulse rounded bg-gray-200" />
              </div>
            ))
          ) : (
            categories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onMouseEnter={() => {
                  setActiveCategory(cat)
                  setDrillStack([])
                }}
                className={`flex items-center justify-between px-3.75 w-full text-left text-wrap h-10.75 rounded-[10px] cursor-pointer text-black transition-colors ${
                  activeCategory?.slug === cat.slug
                    ? 'bg-under-header-color'
                    : 'hover:bg-gray-50'
                }`}
              >
                <span className="font-medium text-sm leading-snug select-auto">{cat.title}</span>
                {(cat.children?.length ?? 0) > 0 && (
                  <HiChevronRight className="w-4 h-4 text-black shrink-0 ml-2" />
                )}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex-1 p-6 overflow-y-auto">
        {loading ? (
          <>
            <div className="mb-6 h-10 w-80 animate-pulse rounded-xl bg-gray-100" />
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 9 }).map((_, index) => (
                <div
                  key={`catalog-dropdown-right-skeleton-${index}`}
                  className="flex items-center gap-3 rounded-lg border border-transparent p-3"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                    <div className="h-4 w-6 animate-pulse rounded bg-gray-200" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="h-4 w-5/6 animate-pulse rounded bg-gray-100" />
                    <div className="mt-2 h-4 w-2/3 animate-pulse rounded bg-gray-100" />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : activeCategory ? (
          <>
            <div className="flex items-center gap-2 mb-4">
              {drillStack.length > 0 && (
                <button
                  type="button"
                  onClick={goBack}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 select-none cursor-pointer"
                >
                  <HiChevronLeft className="w-4 h-4" />
                  <span>Назад</span>
                </button>
              )}
              <h2 className="text-2xl font-semibold text-gray-900 select-none">
                {currentParent?.title}
              </h2>
            </div>

            {shownChildren.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {shownChildren.map((child) => {
                  const hasChildren = (child.children?.length ?? 0) > 0
                  const initials = child.title
                    .split(' ')
                    .slice(0, 2)
                    .map((w) => w[0]?.toUpperCase() ?? '')
                    .join('')

                  if (hasChildren) {
                    return (
                      <div
                        key={child.slug}
                        className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors group w-full text-left border border-transparent hover:border-gray-200"
                      >
                        <Link
                          to={withWarehouse(`/catalog?category=${child.slug}`)}
                          onClick={onClose}
                          className="flex flex-1 min-w-0 items-center gap-3"
                        >
                          <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center group-hover:bg-primary-light transition-colors">
                            <span className="text-xs font-semibold text-gray-400 select-none">
                              {initials}
                            </span>
                          </div>
                          <span className="flex-1 text-base text-black transition-colors leading-snug truncate">
                            {child.title}
                          </span>
                        </Link>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            setDrillStack((prev) => [...prev, child])
                          }}
                          className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0"
                          aria-label="Внутрь"
                        >
                          <HiChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )
                  }

                  return (
                    <Link
                      key={child.slug}
                      to={withWarehouse(`/catalog?category=${child.slug}`)}
                      onClick={onClose}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center group-hover:bg-primary-light transition-colors">
                        <span className="text-xs font-semibold text-gray-400 select-none">
                          {initials}
                        </span>
                      </div>
                      <span className="flex-1 text-base text-black transition-colors leading-snug">
                        {child.title}
                      </span>
                    </Link>
                  )
                })}
              </div>
            ) : (
              <Link
                to={withWarehouse(currentParent?.slug ? `/catalog?category=${currentParent.slug}` : '/catalog')}
                onClick={onClose}
                className="inline-flex items-center gap-1 text-base text-black hover:underline font-medium"
              >
                Перейти в категорию
                <HiChevronRight className="w-4 h-4" />
              </Link>
            )}
          </>
        ) : null}
      </div>
    </div>
  )
}

export default CatalogDropdown
